import { useCallback, useEffect, useMemo, useState } from "react";
import { BffClient } from "@made-people/gears-bff-client";
import {
  useGearsConfigurationVariable,
  useGearsServiceUrl,
} from "@made-people/gears-configuration";
import { useHref } from "@made-people/grease-link";
import { useFrontendContext } from "../../../hooks/use-frontend-context";
import { frontendProductToGreaseProduct } from "../../../services/bff/mappers/frontend-product-to-grease-product";
import { frontendProductToGreaseRelatedProducts } from "../../../services/bff/mappers/frontend-product-to-grease-related-products";
import { getAttributeBooleanValue } from "../helpers";
import { getStoryblokAssetUrl } from "../settings";
import type {
  Product,
  ProductBase,
  RelatedProduct,
} from "@made-people/grease-product";
import type { CustomisationFeature } from "../settings";

export const useCustomisationFeaturesState = (props: {
  productBase: ProductBase;
  relatedProducts: RelatedProduct[];
  features: CustomisationFeature[];
  onTargetProductChange: (product: Product) => void;
  onUrlChange: (url: string) => void;
  onNoticeChange: (active: boolean) => void;
}) => {
  const featureRelationMap: Record<string, string> = {
    sidewing: "sidewing-variant",
    lace: "lace-variant",
    wiring: "wiring-variant",
  };
  const href = useHref();
  const frontendContext = useFrontendContext();
  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");
  const bffClient = useMemo(
    () => new BffClient({ baseUrl: bffUrl, tenantId }),
    [bffUrl, tenantId],
  );

  const [currentRelatedProducts, setCurrentRelatedProducts] = useState(
    props.relatedProducts,
  );
  const [selectedFeature, setSelectedFeature] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    setCurrentRelatedProducts(props.relatedProducts);
  }, [props.productBase, props.relatedProducts]);

  useEffect(() => {
    setSelectedFeature({
      sidewing: getAttributeBooleanValue(props.productBase, "hasSideWing"),
      lace: getAttributeBooleanValue(props.productBase, "hasLace"),
      wiring: getAttributeBooleanValue(props.productBase, "isWired"),
    });
  }, [props.productBase.productId]);

  const fetchProductViewModel = useCallback(
    async (productId: string) => {
      const result = await bffClient.fetchFrontendProductsCached(
        [productId],
        frontendContext,
      );
      if (!result?.frontendProducts.length) {
        return undefined;
      }

      return {
        product: frontendProductToGreaseProduct(
          result.frontendProducts[0],
          frontendContext,
        ),
        relatedProducts: frontendProductToGreaseRelatedProducts(
          result.frontendProducts[0],
          frontendContext,
        ),
      };
    },
    [bffClient, frontendContext],
  );

  const relatedProductExists = useCallback(
    (featureName: string) => {
      const relationType = featureRelationMap[featureName];
      if (!relationType) {
        return false;
      }
      return currentRelatedProducts.some(
        (relatedProduct) => relatedProduct.relationType === relationType,
      );
    },
    [currentRelatedProducts, featureRelationMap],
  );

  const featureIsSelected = useCallback(
    (feature: CustomisationFeature) => {
      return Boolean(selectedFeature[feature.featureName]);
    },
    [selectedFeature],
  );

  useEffect(() => {
    const hasMissingProducts = props.features.some(
      (feature) => !relatedProductExists(feature.featureName),
    );
    props.onNoticeChange(hasMissingProducts);
  }, [props.features, props.onNoticeChange, relatedProductExists]);

  const toggleProduct = useCallback(
    async (featureName: string) => {
      const relationType = featureRelationMap[featureName];
      const relatedProduct = relationType
        ? currentRelatedProducts.find(
            (related) => related.relationType === relationType,
          )?.product
        : undefined;

      if (relatedProduct) {
        await fetchProductViewModel(relatedProduct.base.productId).then(
          (updatedProduct) => {
            if (!updatedProduct) {
              return;
            }
            setCurrentRelatedProducts(updatedProduct.relatedProducts);
            props.onTargetProductChange(updatedProduct.product);
            props.onUrlChange(href(updatedProduct.product.base.slug));
          },
        );
      }

      setSelectedFeature((prev) => ({
        ...prev,
        [featureName]: !prev[featureName],
      }));
    },
    [
      currentRelatedProducts,
      featureRelationMap,
      fetchProductViewModel,
      href,
      props,
    ],
  );

  const items = useMemo(() => {
    return props.features.map((feature) => {
      const isSelected = featureIsSelected(feature);
      const imageUrl = getStoryblokAssetUrl(feature.image);
      const inactiveImageUrl = getStoryblokAssetUrl(feature.imageFalse);
      const imageAlt =
        !isSelected && feature.imageFalse?.alt
          ? feature.imageFalse.alt
          : (feature.image?.alt ?? "");
      const canToggle = relatedProductExists(feature.featureName);

      return {
        feature,
        isSelected,
        canToggle,
        imageUrl,
        inactiveImageUrl,
        imageAlt,
      };
    });
  }, [featureIsSelected, props.features, relatedProductExists]);

  return { items, toggleProduct };
};
